import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Shield,
  Smartphone,
  Key,
  Copy,
  Check,
  AlertTriangle,
  ArrowLeft,
  QrCode,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { api } from '../../lib/api';

interface TwoFactorSetupData {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

interface VerifyResponse {
  success: boolean;
  backupCodes: string[];
}

export function TwoFactorSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'intro' | 'setup' | 'verify' | 'backup' | 'complete'>('intro');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);

  // Check 2FA status
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: async () => {
      const response = await api.get('/auth/2fa/status');
      return response.data;
    },
  });

  // Enable 2FA mutation
  const enableMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/2fa/enable');
      return response.data as TwoFactorSetupData;
    },
    onSuccess: () => {
      setStep('setup');
    },
  });

  // Verify mutation
  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.post('/auth/2fa/verify', { code });
      return response.data as VerifyResponse;
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setStep('backup');
    },
  });

  // Disable 2FA mutation
  const disableMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.post('/auth/2fa/disable', { code });
      return response.data;
    },
    onSuccess: () => {
      navigate('/banking/security');
    },
  });

  const handleCopySecret = () => {
    if (enableMutation.data?.secret) {
      navigator.clipboard.writeText(enableMutation.data.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopiedBackup(true);
    setTimeout(() => setCopiedBackup(false), 2000);
  };

  const handleVerify = () => {
    if (verificationCode.length === 6) {
      verifyMutation.mutate(verificationCode);
    }
  };

  if (statusLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Autenticação em Duas Etapas</h1>
            <p className="text-surface-400">Configure a segurança da sua conta</p>
          </div>
        </div>
        <Card className="max-w-lg mx-auto p-6">
          <div className="animate-pulse space-y-4">
            <div className="w-16 h-16 bg-surface-700 rounded-full mx-auto" />
            <div className="h-6 bg-surface-700 rounded w-1/2 mx-auto" />
            <div className="h-4 bg-surface-700/50 rounded w-3/4 mx-auto" />
          </div>
        </Card>
      </div>
    );
  }

  // If 2FA is already enabled, show disable option
  if (status?.enabled && step === 'intro') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Autenticação em Duas Etapas</h1>
            <p className="text-surface-400">Gerencie a segurança da sua conta</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/banking/security')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
        <Card className="max-w-lg mx-auto p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">2FA Ativado</h2>
            <p className="text-surface-400 mt-2">
              Sua conta está protegida com autenticação em duas etapas.
            </p>
          </div>

          <div className="space-y-4">
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => navigate('/banking/security')}
            >
              Ver Configurações de Segurança
            </Button>
            
            <div className="border-t border-surface-700 pt-4">
              <p className="text-sm text-surface-400 mb-3">
                Para desativar o 2FA, insira o código do seu aplicativo autenticador:
              </p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="000000"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-lg tracking-widest"
                />
                <Button
                  variant="danger"
                  onClick={() => disableMutation.mutate(verificationCode)}
                  disabled={verificationCode.length !== 6 || disableMutation.isPending}
                >
                  Desativar
                </Button>
              </div>
              {disableMutation.isError && (
                <p className="text-red-400 text-sm mt-2">
                  Código inválido. Tente novamente.
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Autenticação em Duas Etapas</h1>
          <p className="text-surface-400">Configure a segurança da sua conta</p>
        </div>
        <Button variant="ghost" onClick={() => navigate('/banking/security')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
      </div>
      <Card className="max-w-lg mx-auto p-6">
        {/* Step: Intro */}
        {step === 'intro' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-primary-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Proteja sua conta
            </h2>
            <p className="text-surface-400 mb-6">
              A autenticação em duas etapas adiciona uma camada extra de segurança à sua conta.
              Você precisará de um aplicativo autenticador como Google Authenticator ou Authy.
            </p>

            <div className="space-y-3 text-left mb-6">
              <div className="flex items-start gap-3 p-3 bg-surface-800 rounded-lg">
                <Smartphone className="w-5 h-5 text-primary-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Aplicativo Autenticador</p>
                  <p className="text-xs text-surface-400">
                    Baixe o Google Authenticator ou Authy no seu celular
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-surface-800 rounded-lg">
                <QrCode className="w-5 h-5 text-primary-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Escaneie o QR Code</p>
                  <p className="text-xs text-surface-400">
                    Use o aplicativo para escanear o código QR
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-surface-800 rounded-lg">
                <Key className="w-5 h-5 text-primary-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Códigos de Backup</p>
                  <p className="text-xs text-surface-400">
                    Guarde os códigos de backup em local seguro
                  </p>
                </div>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={() => enableMutation.mutate()}
              disabled={enableMutation.isPending}
            >
              {enableMutation.isPending ? 'Configurando...' : 'Começar Configuração'}
            </Button>
          </div>
        )}

        {/* Step: Setup */}
        {step === 'setup' && enableMutation.data && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-4 text-center">
              Escaneie o QR Code
            </h2>
            
            <div className="bg-white p-4 rounded-lg mb-4 flex justify-center">
              {enableMutation.data.qrCodeDataUrl ? (
                <img
                  src={enableMutation.data.qrCodeDataUrl}
                  alt="QR Code"
                  className="w-48 h-48"
                />
              ) : (
                <div className="w-48 h-48 flex items-center justify-center bg-gray-100 text-gray-500">
                  QR Code
                </div>
              )}
            </div>

            <p className="text-sm text-surface-400 text-center mb-4">
              Escaneie este código com seu aplicativo autenticador
            </p>

            <div className="bg-surface-800 p-3 rounded-lg mb-6">
              <p className="text-xs text-surface-400 mb-1">Ou insira manualmente:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm text-primary-400 font-mono break-all">
                  {enableMutation.data.secret}
                </code>
                <Button variant="ghost" size="sm" onClick={handleCopySecret}>
                  {copiedSecret ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <Button className="w-full" onClick={() => setStep('verify')}>
              Continuar
            </Button>
          </div>
        )}

        {/* Step: Verify */}
        {step === 'verify' && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-4 text-center">
              Verificar Código
            </h2>
            
            <p className="text-surface-400 text-center mb-6">
              Digite o código de 6 dígitos do seu aplicativo autenticador
            </p>

            <Input
              type="text"
              placeholder="000000"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
              className="text-center text-2xl tracking-widest mb-4"
            />

            {verifyMutation.isError && (
              <div className="flex items-center gap-2 text-red-400 text-sm mb-4">
                <AlertTriangle className="w-4 h-4" />
                Código inválido. Tente novamente.
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setStep('setup')} className="flex-1">
                Voltar
              </Button>
              <Button
                onClick={handleVerify}
                disabled={verificationCode.length !== 6 || verifyMutation.isPending}
                className="flex-1"
              >
                {verifyMutation.isPending ? 'Verificando...' : 'Verificar'}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Backup Codes */}
        {step === 'backup' && (
          <div>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-yellow-400" />
              </div>
              <h2 className="text-xl font-semibold text-white">Códigos de Backup</h2>
              <p className="text-surface-400 mt-2">
                Guarde estes códigos em local seguro. Cada código pode ser usado apenas uma vez.
              </p>
            </div>

            <div className="bg-surface-800 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <code
                    key={index}
                    className="text-sm font-mono text-primary-400 bg-surface-900 p-2 rounded text-center"
                  >
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <Button
              variant="secondary"
              className="w-full mb-4"
              onClick={handleCopyBackupCodes}
            >
              {copiedBackup ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-400" />
                  Copiado!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Códigos
                </>
              )}
            </Button>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <p className="text-sm text-yellow-200">
                  Se você perder acesso ao seu aplicativo autenticador, precisará destes códigos para acessar sua conta.
                </p>
              </div>
            </div>

            <Button className="w-full" onClick={() => setStep('complete')}>
              Guardei os códigos
            </Button>
          </div>
        )}

        {/* Step: Complete */}
        {step === 'complete' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              2FA Ativado com Sucesso!
            </h2>
            <p className="text-surface-400 mb-6">
              Sua conta agora está protegida com autenticação em duas etapas.
            </p>

            <Button className="w-full" onClick={() => navigate('/banking/security')}>
              Concluir
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

export default TwoFactorSetup;
